/**
 * CMS-09 "Importar CSV · validación en seco": the file is always validated first
 * (`POST /cms/grants { mode:'bulk', dryRun:true }`), the report is rendered row by row, and only
 * then does "Importar N filas" write anything. Rows with an error are skipped by the API.
 */
import type { CmsGrantCreateResponse, GrantCsvRowDTO } from '@akpl/shared';
import { GRANT_CSV_COLUMNS } from '@akpl/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useRef, useState } from 'react';

import { api } from '../../api';
import { useT } from '../../i18n';
import { formatNumber } from '../../lib/format';
import { Button } from '../ui/button';
import { Drawer } from '../ui/drawer';
import { Badge } from '../ui/panel';
import { useToast } from '../ui/toast';
import { CsvResultBadge } from './badges';

interface DryRun {
  rows: GrantCsvRowDTO[];
  valid: number;
  warnings: number;
  errors: number;
}

/**
 * Rows the import would actually create.
 *
 * QA 2026-09-15 **UX-569**: re-running an already-imported file marked all five rows «Aviso: ya
 * existe» and then still offered «Importar 5 filas» — a button whose press is a no-op the
 * operator cannot predict. `valid` counts every row the API would accept, duplicates included;
 * what the button promises is the rows that are *new*.
 */
export function newRowCount(rows: GrantCsvRowDTO[]): number {
  return rows.filter((row) => row.status !== 'error' && row.issue !== 'already_exists').length;
}

function summarize(result: Extract<CmsGrantCreateResponse, { mode: 'bulk' }>): DryRun {
  return {
    rows: result.rows,
    valid: result.valid,
    warnings: result.warnings,
    errors: result.errors.length,
  };
}

export function CsvImportDrawer({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useT();
  const locale = i18n.language;
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  /**
   * Which pick a dry run belongs to. Two picks in quick succession are two requests, and the
   * first one answering last must not write its verdict over the second one's (UX-573).
   */
  const pick = useRef(0);
  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [report, setReport] = useState<DryRun | null>(null);
  const [imported, setImported] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const dryRun = useMutation({
    mutationFn: ({ text }: { text: string; pick: number }) =>
      api.cmsGrantCreate({ body: { mode: 'bulk', csv: text, dryRun: true } }),
    onSuccess: (result, vars) => {
      if (vars.pick !== pick.current) return;
      if (result.mode !== 'bulk') return;
      setReport(summarize(result));
    },
    /**
     * QA 2026-09-15 **UX-573**: a rejected pick left the *previous* file's verdict on screen —
     * its row table, its "2 válidas · 0 avisos · 0 errores" and an enabled «Importar 2 filas» —
     * under the new file's name. The refusal clears the report and the parsed CSV as well as
     * showing the error, so there is nothing left to import.
     */
    onError: (_err, vars) => {
      if (vars.pick !== pick.current) return;
      setReport(null);
      setCsv(null);
      setError(t('cms:grants.csv.headerMismatch'));
    },
  });

  const runImport = useMutation({
    mutationFn: () => api.cmsGrantCreate({ body: { mode: 'bulk', csv: csv!, dryRun: false } }),
    onSuccess: (result) => {
      if (result.mode !== 'bulk') return;
      setImported(result.created);
      setReport(summarize(result));
      // UX-562: the console announced destroying things and not creating them.
      toast({ message: t('cms:grants.toast.imported', { count: result.created }) });
      void queryClient.invalidateQueries({ queryKey: ['cms', 'grants'] });
    },
    onError: () => setError(t('common:states.error')),
  });

  /**
   * Every pick is a new dry run over a blank slate (UX-573): the report, the parsed CSV and any
   * previous error go **before** the request leaves, so no state of the last file can survive
   * this one. The input is also reset, so picking the same file twice re-parses it instead of
   * being swallowed as "no change".
   */
  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const seq = (pick.current += 1);
    setError(null);
    setImported(null);
    setReport(null);
    setCsv(null);
    setFileName(file.name);
    const text = await file.text();
    if (seq !== pick.current) return;
    setCsv(text);
    dryRun.mutate({ text, pick: seq });
  };

  const rows = report?.rows ?? [];
  const importable = newRowCount(rows);

  return (
    <Drawer
      open
      width={640}
      onClose={onClose}
      title={t('cms:grants.csv.title')}
      subtitle={
        report
          ? `${t('cms:grants.csv.fileMeta', { name: fileName, rows: formatNumber(rows.length, locale) })} · ${
              imported === null
                ? t('cms:grants.csv.notWritten')
                : t('cms:grants.csv.imported', { count: imported })
            }`
          : fileName || undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={!report || importable === 0 || runImport.isPending || imported !== null}
            onClick={() => runImport.mutate()}
          >
            {t('cms:grants.csv.import', { count: importable })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="rounded-[10px] border border-line bg-canvas px-3 py-2.5">
          <p className="text-[12px] font-semibold tracking-wide text-muted uppercase">
            {t('cms:grants.csv.expectedHeader')}
          </p>
          <p className="mt-1 font-mono text-[12px] break-all text-ink">
            {GRANT_CSV_COLUMNS.join(',')}
          </p>
        </div>

        <div>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            // `hidden` as well as the class: it is what tells the drawer's focus logic to skip
            // this input, with no stylesheet involved (UX-531).
            hidden
            className="hidden"
            data-testid="grants-csv-input"
            onChange={(event) => void onFile(event)}
          />
          <Button variant="secondary" size="sm" onClick={() => fileInput.current?.click()}>
            {t('cms:grants.csv.pickFile')}
          </Button>
          {/* UX-573: the refusal belongs next to the file that was refused, not at the foot of
              a panel the operator has to scroll to. */}
          {error && (
            <p className="mt-2 text-[12px] text-danger" role="alert" data-testid="csv-error">
              {error}
            </p>
          )}
        </div>

        {report && (
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="ok">{t('cms:grants.csv.valid', { count: report.valid })}</Badge>
            <Badge tone="warn">{t('cms:grants.csv.warnings', { count: report.warnings })}</Badge>
            <Badge tone="danger">{t('cms:grants.csv.errors', { count: report.errors })}</Badge>
            <span className="ml-auto text-[12px] text-muted">
              {importable === 0 && report.valid > 0
                ? t('cms:grants.csv.nothingNew')
                : t('cms:grants.csv.skipped')}
            </span>
          </div>
        )}

        {rows.length > 0 ? (
          <div className="overflow-x-auto rounded-[12px] border border-line">
            <table className="w-full border-collapse text-left text-[13px]">
              <thead>
                <tr className="border-b border-line bg-canvas/60 text-[12px] font-semibold tracking-wide text-muted uppercase">
                  <th scope="col" className="px-3 py-2">
                    {t('cms:grants.csv.row')}
                  </th>
                  <th scope="col" className="px-3 py-2">
                    {t('cms:grants.csv.type')}
                  </th>
                  <th scope="col" className="px-3 py-2">
                    {t('cms:grants.csv.matchValue')}
                  </th>
                  <th scope="col" className="px-3 py-2">
                    {t('cms:grants.csv.days')}
                  </th>
                  <th scope="col" className="px-3 py-2">
                    {t('cms:grants.csv.result')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.row} className="border-b border-line last:border-0">
                    <td className="px-3 py-2 tabular-nums text-muted">{row.row}</td>
                    <td className="px-3 py-2">{row.matchType ?? '—'}</td>
                    <td className="px-3 py-2 break-all">{row.matchValue ?? '—'}</td>
                    <td className="px-3 py-2 tabular-nums">{row.durationDays ?? '—'}</td>
                    <td className="px-3 py-2">
                      <CsvResultBadge status={row.status} issue={row.issue} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-6 text-center text-[12px] text-muted">{t('cms:grants.csv.empty')}</p>
        )}

        <p className="rounded-[10px] bg-cyan/10 px-3 py-2.5 text-[12px] text-ink">
          {t('cms:grants.csv.note')}
        </p>
      </div>
    </Drawer>
  );
}
